import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {  useNavigate } from "react-router-dom";
import authManager from "../services/auth/AuthManager";
import localStorageUtil from "../utility/localStorageUtil";
import React,{ useState } from "react";
import ToastNotification, { showToast } from "../components/Toaster";

import LoginImage from "../assets/login.jpg";
import BrandLogo from "./BrandLogo";

const schema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email format")
      .required("Please enter your username"),
    password: yup.string().required("Please enter your password"),
  })
  .required();

export default function Login() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const renderFieldError = (message?: string) => (
    <div className="mt-1 min-h-[20px]">
      {message ? <p className="text-sm text-red-600">{message}</p> : null}
    </div>
  );

  const handleLogin = (data: any) => {
    setLoading(true);
    authManager
      .login(data.email, data.password, true)
      .then(async (result) => {
        if (result.message === "error" && !result.success) {
          setErrorMessage("Login failed. Please check your credentials.");
          setLoading(false);
          if (
            localStorageUtil.getLocalStorage("currentUrl") !== "/adminlogin" &&
            localStorageUtil.getLocalStorage("currentUrl") !== "/logout"
          ) {
            const url = localStorageUtil.getLocalStorage("currentUrl");
            if (url) navigate(`${url}`);
          }
        } else {        
          setLoading(false);
          navigate("/superadmin");
        }
      })
      .catch((err) => {
        console.error("handleSubmit error:", err);
        showToast(err.message, "error");
        setErrorMessage("Login failed. Please check your credentials.");
        setLoading(false);
      });
  };

  return (
    <div className="flex min-h-screen bg-ez-base">
      <ToastNotification/>

      {/* Left: Hero Image */}
      <div
        className="hidden lg:block w-1/2 bg-cover bg-center"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />

      {/* Right: Login Form */}
      <div className="flex w-full items-center justify-center p-8 bg-ez-base lg:w-1/2">
        <div className="w-full sm:w-[420px] rounded-lg border border-ez-border bg-ez-surface shadow-card p-8">

          {/* Logo + Title */}
          <div className="flex flex-row items-center mb-8">
            <BrandLogo className="h-12 w-12" appBrand="vz" />
            <h2 className="ml-3 text-2xl font-bold text-ez-text-primary">
              Admin Sign in
            </h2>
          </div>

          <form onSubmit={handleSubmit(handleLogin)} noValidate className="space-y-1">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ez-text-primary mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                {...register("email")}
                type="email"
                autoComplete="email"
                placeholder="admin@example.com"
                style={{ borderColor: errors.email ? "#dc2626" : undefined }}
                className="w-full min-h-[44px] rounded-md border bg-ez-surface text-ez-text-primary placeholder-ez-text-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-600"
              />
              {renderFieldError(errors.email?.message)}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ez-text-primary mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  style={{ borderColor: errors.password ? "#dc2626" : undefined }}
                  className="w-full min-h-[44px] rounded-md border bg-ez-surface text-ez-text-primary placeholder-ez-text-muted px-3 py-2.5 text-sm pr-11 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-600"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-ez-text-muted hover:text-ez-text-primary transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {renderFieldError(errors.password?.message)}
            </div>

            {/* Error */}
            <div className="min-h-[16px]">
              {errorMessage && (
                <p className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-600 dark:text-red-400">
                  {errorMessage}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-2.5 px-4 rounded-md bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 text-white font-semibold text-sm tracking-wide shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Verifying..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
